import { badgeBase } from '../ui/queueUi'
import type { SupportAgent, SupportTicketStatus } from '../../lib/api/support'

/**
 * Support-ticket vocabulary shared by the queue and the ticket workspace.
 * Color discipline mirrors the moderation queue: red = a customer is blocked on us,
 * amber = we are waiting on someone else, neutral = nothing to do, green = resolved.
 */

export const normalizeStatus = (status: SupportTicketStatus): SupportTicketStatus => {
  switch (status) {
    case 'Open':
      return 'WaitingForAgent'
    case 'WaitingOnCustomer':
      return 'WaitingForCustomer'
    default:
      return status
  }
}

export const ticketStatusOptions: SupportTicketStatus[] = [
  'New',
  'Reviewed',
  'Assigned',
  'WaitingForAgent',
  'WaitingForCustomer',
  'WaitingForDeveloper',
  'Resolved'
]

export type WorkflowBucket = 'needs_reply' | 'waiting' | 'resolved' | 'all'

export const workflowBucketOptions: Array<{ value: WorkflowBucket; label: string }> = [
  { value: 'needs_reply', label: 'Needs reply' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'all', label: 'All' }
]

/** Statuses where the next move is ours: the customer is waiting on an agent. */
export const NEEDS_REPLY_STATUSES: SupportTicketStatus[] = ['New', 'Reviewed', 'Assigned', 'WaitingForAgent']

/** Statuses where the ball is in someone else's court. */
export const WAITING_STATUSES: SupportTicketStatus[] = ['WaitingForCustomer', 'WaitingForDeveloper']

export const ACTIVE_STATUSES: SupportTicketStatus[] = [...NEEDS_REPLY_STATUSES, ...WAITING_STATUSES]

export const bucketForStatus = (status: SupportTicketStatus): Exclude<WorkflowBucket, 'all'> => {
  const normalized = normalizeStatus(status)
  if (normalized === 'Resolved') return 'resolved'
  if (WAITING_STATUSES.includes(normalized)) return 'waiting'
  return 'needs_reply'
}

export const statusLabel = (status: SupportTicketStatus) => {
  switch (normalizeStatus(status)) {
    case 'New':
      return 'New'
    case 'Reviewed':
      return 'Reviewed'
    case 'Assigned':
      return 'Assigned'
    case 'WaitingForAgent':
      return 'Needs reply'
    case 'WaitingForCustomer':
      return 'Waiting on customer'
    case 'WaitingForDeveloper':
      return 'With developers'
    case 'Resolved':
      return 'Resolved'
    default:
      return status
  }
}

const statusBadgeClasses = (status: SupportTicketStatus) => {
  switch (normalizeStatus(status)) {
    case 'New':
    case 'WaitingForAgent':
      return 'bg-rose-100 text-rose-800'
    case 'WaitingForCustomer':
    case 'WaitingForDeveloper':
      return 'bg-amber-100 text-amber-800'
    case 'Resolved':
      return 'bg-emerald-100 text-emerald-800'
    default:
      return 'bg-stone-100 text-stone-600'
  }
}

export function TicketStatusBadge({ status }: { status: SupportTicketStatus }) {
  return <span className={`${badgeBase} ${statusBadgeClasses(status)}`}>{statusLabel(status)}</span>
}

/** Left-rail color for a queue row: how urgently this ticket needs a human. */
export const ticketRailClass = (status: SupportTicketStatus) => {
  switch (bucketForStatus(status)) {
    case 'needs_reply':
      return 'border-l-rose-500'
    case 'waiting':
      return 'border-l-amber-400'
    default:
      return 'border-l-stone-300'
  }
}

export const ticketRef = (id: string) => `#${id.slice(-6).toUpperCase()}`

export function PlanBadge({ tier, status }: { tier?: string | null; status?: string | null }) {
  if (status === 'Active' || status === 'Canceled' || status === 'PastDue') {
    const tierLabel = tier === 'PremiumAnnual' ? 'Annual' : tier === 'PremiumMonthly' ? 'Monthly' : 'Paid'
    const suffix = status === 'PastDue' ? ' · past due' : status === 'Canceled' ? ' · canceled' : ''
    const classes = status === 'PastDue' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
    return <span className={`${badgeBase} ${classes}`}>{tierLabel + suffix}</span>
  }
  if (status === 'Trial') {
    return <span className={`${badgeBase} bg-indigo-100 text-indigo-800`}>Trial</span>
  }
  return <span className={`${badgeBase} bg-stone-100 text-stone-500`}>Free</span>
}

export const predefinedTags = ['High Priority', 'Billing Issue', 'Feedback', 'Bug Report', 'Feature Request', 'Account Issue']

/** Tags stay quiet outlines; only High Priority earns color. */
export const tagChipClass = (tag: string) =>
  tag.toLowerCase() === 'high priority'
    ? 'border-red-200 bg-red-50 text-red-700'
    : 'border-stone-200 bg-transparent text-stone-500'

export const memberSinceLabel = (userCreatedAt?: string | null) => {
  if (!userCreatedAt) return null
  const then = new Date(userCreatedAt).getTime()
  if (Number.isNaN(then)) return null
  const months = Math.floor((Date.now() - then) / (30.44 * 24 * 36e5))
  if (months < 1) return 'member <1mo'
  if (months < 12) return `member ${months}mo`
  return `member ${Math.floor(months / 12)}y`
}

export const supportAgentLabel = (agent: SupportAgent) => agent.displayName?.trim() || `${agent.role} (${agent.id.slice(-6)})`

export const agentNameById = (agents: SupportAgent[], agentId?: string | null) => {
  if (!agentId) return null
  const agent = agents.find((candidate) => candidate.id === agentId)
  return agent ? supportAgentLabel(agent) : `agent ${agentId.slice(-6)}`
}

export const customerEmailHref = (email: string, ticketId: string) => {
  const subject = `HikeStrong support ticket ${ticketRef(ticketId)}`
  return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}`
}
